/**
 * Wall topology commands (docs/bim-plan/06 Parts 1 & 4, Phase 2.6a): split, weld,
 * location-line changes, and flip. These create/delete elements and re-host openings, so
 * they live beside `elements.ts`, not in the sub-element `geometryOps` surface. Each is
 * ONE Yjs transaction with the session origin (one command = one undo step); refs resolve
 * at execution time and fail soft.
 *
 * CORNER RENDERING stays fully DERIVED at the visual level (endpoint coincidence + group
 * union at regen — `cornerWeld.ts`) — that mitering never needed a stored relationship,
 * and still doesn't.
 *
 * WALL JOINS (Matt, 2026-09-21) are a SEPARATE, real, stored parametric relationship
 * layered on top — `startJoin`/`endJoin` on the wall schema, propagated through
 * `geometryOps.ts`'s `planWallJoinPropagation`/`applyJoinPropagation` (see that file's
 * "WALL JOINS" section for the full doctrine). `cornerJoin` and `setLocationLine` below
 * are wired into it: a corner-join or location-line change that moves an end already
 * joined to some OTHER wall propagates that move for real, and refuses (mutating
 * nothing) if it would collapse a joined wall. `splitWall`/`joinWalls`/
 * `splitWallWithGap`/`offsetCopyWall` are NOT wired in — they change element identity
 * (new ids on split, ids deleted on weld), which a join ref can go stale against. A
 * stale ref is inert, not dangerous (`computeJoinPropagation` skips any target that no
 * longer resolves to a wall), but it is a known v1 gap: split/weld a joined wall and its
 * join can silently stop tracking anything. Re-pointing join refs through those
 * identity-changing ops is future work, not attempted here.
 */
import * as Y from 'yjs'
import type { ElementId, NodeId, ParamValue, Path2D, PathNode, Pt2 } from '../types'
import { newNodeId } from '../ids'
import {
  getAssembly, getElement, getType, hostedElements, maps, recordToYMap, transact,
  type ModelDoc,
} from '../doc'
import { newElementId } from '../ids'
import { clonePath, nodeIndex, offsetPolyline, pathLength, type EdgeRef, edgeIndices } from '../pathOps'
import { centerlineOffsetFt, DEFAULT_LOCATION_LINE, type LocationLine } from '../locationLine'
import { pathParamKeyFor } from '../registry'
import { moveNode, rehostOpeningsForPathChange, planWallJoinPropagation, applyJoinPropagation, autoDetectWallJoinAtNode } from './geometryOps'

/** Endpoint-coincidence tolerance for weld chain assembly (06 §1.2). */
const CHAIN_EPS = 1e-6

export type SplitAt = { nodeId: NodeId } | { edge: EdgeRef; pt: Pt2 } | 'explode'

export type JoinResult = { ok: true; id: ElementId } | { ok: false; reason: string }

function segLengths(nodes: PathNode[]): number[] {
  const out: number[] = []
  for (let i = 0; i < nodes.length - 1; i++) {
    out.push(Math.hypot(nodes[i + 1].pt[0] - nodes[i].pt[0], nodes[i + 1].pt[1] - nodes[i].pt[1]))
  }
  return out
}

function readParams(em: Y.Map<unknown>): Record<string, ParamValue> {
  const pm = em.get('params') as Y.Map<unknown> | undefined
  const out: Record<string, ParamValue> = {}
  if (pm) pm.forEach((v, k) => { out[k] = v as ParamValue })
  return out
}

/**
 * Split a polyline wall into independent segment elements (06 §1.2 item 2).
 * `at`: { nodeId } (interior node) | { edge, pt } (insert a node there first) |
 * 'explode' (every interior node). The ORIGINAL element keeps its id and the first
 * sub-path (selection, palette state, and first-run hosted refs stay stable); each new
 * element clones typeId/levelId/instance params and keeps its run's existing node ids —
 * only the duplicated boundary node gets a fresh id on the later side. Hosted openings
 * re-host by exact arc-length arithmetic; an opening exactly on a cut belongs to the
 * FOLLOWING segment (half-open intervals). Returns the chain-ordered element ids, or
 * null when the split is invalid (unknown ref, end node, closed path, hosted element).
 */
export function splitWall(model: ModelDoc, elementId: ElementId, at: SplitAt): ElementId[] | null {
  const { elements } = maps(model)
  const em = elements.get(elementId)
  if (!em) return null
  if (em.get('category') !== 'wall') return null
  const rec = getElement(model, elementId)
  if (!rec) return null
  if (rec.params.pinned === true) return null   // MODIFY PHASE-2 (Pin)
  const path = rec.params.path as Path2D | null
  if (!path || typeof path !== 'object' || !Array.isArray(path.nodes)) return null
  // FIX (Matt's report, 2026-08-09 — "no matter what, we get an error message"): this
  // used to require 3+ nodes, which refuses splitAtPoint on every plain 2-node
  // straight-run wall — the COMMON case under Segments draw mode (one wall per
  // straight run, ArchiCAD default). Splitting an edge inserts its OWN node, so it
  // only needs one edge to exist (2+ nodes); splitAtNode/explode already self-guard
  // via nodeIndex bounds / an empty cuts[] below, so 2 is the true floor.
  if (path.closed || path.nodes.length < 2) return null

  // Build the final node list + cut indices (interior positions in that list).
  let nodes: PathNode[] = clonePath(path).nodes
  let cuts: number[]
  if (at === 'explode') {
    cuts = nodes.slice(1, -1).map((_, i) => i + 1)
  } else if ('nodeId' in at) {
    const i = nodeIndex(path, at.nodeId)
    if (i <= 0 || i >= nodes.length - 1) return null
    cuts = [i]
  } else {
    const idx = edgeIndices(path, at.edge)
    if (!idx) return null
    const inserted: PathNode = { id: newNodeId(), pt: [at.pt[0], at.pt[1]] }
    nodes = [...nodes.slice(0, idx[0] + 1), inserted, ...nodes.slice(idx[0] + 1)]
    cuts = [idx[0] + 1]
  }
  if (!cuts.length) return null

  // Partition into runs; boundary nodes are duplicated (original id stays with the
  // EARLIER run; the later run's copy gets a fresh id).
  const bounds = [0, ...cuts, nodes.length - 1]
  const runs: PathNode[][] = []
  for (let r = 0; r < bounds.length - 1; r++) {
    const from = bounds[r], to = bounds[r + 1]
    const run = nodes.slice(from, to + 1).map((n) => ({ ...n, pt: [n.pt[0], n.pt[1]] as Pt2 }))
    if (r > 0) run[0] = { ...run[0], id: newNodeId() }
    runs.push(run)
  }

  // Prefix arc lengths of each run along the FULL final path.
  const lens = segLengths(nodes)
  const prefixAt = (idx: number) => lens.slice(0, idx).reduce((s, l) => s + l, 0)
  const runStart = bounds.slice(0, -1).map(prefixAt)
  const runEnd = bounds.slice(1).map(prefixAt)

  const openings = hostedElements(model, elementId)
  const baseParams = readParams(em)
  const newIds: ElementId[] = []

  transact(model, () => {
    // original keeps run 0
    const pm = em.get('params') as Y.Map<unknown>
    pm.set('path', { closed: false, nodes: runs[0] })
    const runElementIds: ElementId[] = [elementId]
    for (let r = 1; r < runs.length; r++) {
      const id = newElementId()
      newIds.push(id)
      runElementIds.push(id)
      elements.set(id, recordToYMap({
        id,
        category: 'wall',
        typeId: rec.typeId,
        levelId: rec.levelId,
        // a copy / split / offset keeps the ORIGINAL's phasing (Matt 2026-09-29: "the new
        // element created by the command should have the same phasing properties")
        ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
        ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
        params: { ...baseParams, path: { closed: false, nodes: runs[r] } },
      }))
    }
    // re-host openings by arc length (half-open: exact-cut goes to the following run)
    for (const op of openings) {
      const s = typeof op.params.hostOffsetFt === 'number' ? op.params.hostOffsetFt : 0
      let r = runs.length - 1
      for (let k = 0; k < runs.length; k++) {
        if (s >= runStart[k] - CHAIN_EPS && (s < runEnd[k] - CHAIN_EPS || k === runs.length - 1)) { r = k; break }
      }
      const oem = elements.get(op.id)
      if (!oem) continue
      oem.set('hostId', runElementIds[r])
      const opm = oem.get('params') as Y.Map<unknown>
      opm.set('hostOffsetFt', Math.max(0, s - runStart[r]))
    }
  })
  return [elementId, ...newIds]
}

interface ChainSeg { id: ElementId; nodes: PathNode[]; reversed: boolean; lengthFt: number }

/**
 * Weld contiguous segment walls into one polyline wall (ArchiCAD weld — 06 §1.2 item 3,
 * reworked in the 2.6 polish pass after Matt's field report). Validation REJECTS (with a
 * user-facing reason, mutating nothing): non-walls, mixed levels, mixed types (approved
 * verdict — a weld is a data merge; discarding a segment's type override silently is
 * invisible data loss), branching chains, disjoint sets, and geometrically-incompatible
 * facing (below). The survivor is elementIds[0] — the user's primary selection — and the
 * merged path is oriented to KEEP the survivor's drawing direction, so welding never
 * flips a wall's facing anymore.
 *
 * Facing/location-line compatibility is GEOMETRIC, not param equality (the original
 * equality check rejected legitimate welds after a user Flipped a previously-welded
 * wall): a wall drawn A→B unflipped and the same wall drawn B→A flipped are the SAME
 * physical wall, so each segment's effective centerline offset under chain orientation
 * (delta × −1 when the walk reverses it) is what must match. Centerline walls (delta 0)
 * therefore always weld regardless of flip state; face-referenced walls weld exactly
 * when their bodies lie on the same side of the shared reference line.
 */
export function joinWalls(model: ModelDoc, elementIds: ElementId[]): JoinResult {
  if (!elementIds || elementIds.length < 2) return { ok: false, reason: 'Select at least two walls to join.' }
  const recs: NonNullable<ReturnType<typeof getElement>>[] = []
  for (const id of elementIds) {
    const rec = getElement(model, id)
    if (!rec) return { ok: false, reason: 'A selected wall no longer exists.' }
    if (rec.category !== 'wall') return { ok: false, reason: 'Only walls can be joined.' }
    const path = rec.params.path as Path2D | null
    if (!path || path.closed || !Array.isArray(path.nodes) || path.nodes.length < 2) {
      return { ok: false, reason: 'A selected wall has no joinable path.' }
    }
    if (rec.params.pinned === true) {
      return { ok: false, reason: 'A selected wall is pinned — unpin it to join.' }
    }
    recs.push(rec)
  }
  const first = recs[0]
  if (recs.some((r) => r.levelId !== first.levelId)) {
    return { ok: false, reason: 'Walls on different levels can’t be joined.' }
  }
  if (recs.some((r) => r.typeId !== first.typeId)) {
    const names = [...new Set(recs.map((r) => getType(model, r.typeId)?.name || 'unknown type'))]
    return { ok: false, reason: `Walls have different types (${names.join(' vs ')}) — joining would discard a type override. Retype them to match, then join.` }
  }

  // Chain assembly: match endpoints within EPS, reject branching, walk from a free end.
  const segs: ChainSeg[] = recs.map((r) => {
    const p = r.params.path as Path2D
    return { id: r.id, nodes: clonePath(p).nodes, reversed: false, lengthFt: pathLength(p) }
  })
  const keyOf = (pt: Pt2) => `${Math.round(pt[0] / CHAIN_EPS)}|${Math.round(pt[1] / CHAIN_EPS)}`
  const endpointUse = new Map<string, { seg: number; end: 0 | 1 }[]>()
  segs.forEach((s, i) => {
    const a = keyOf(s.nodes[0].pt), b = keyOf(s.nodes[s.nodes.length - 1].pt)
    if (a === b) return  // degenerate zero-length wall — caught below as disjoint
    for (const [key, end] of [[a, 0], [b, 1]] as const) {
      const list = endpointUse.get(key) || []
      list.push({ seg: i, end })
      endpointUse.set(key, list)
    }
  })
  for (const list of endpointUse.values()) {
    if (list.length > 2) return { ok: false, reason: 'These walls branch — a joined wall must be one continuous chain.' }
  }
  const freeEnds: { seg: number; end: 0 | 1 }[] = []
  for (const list of endpointUse.values()) if (list.length === 1) freeEnds.push(list[0])
  if (freeEnds.length !== 2 && freeEnds.length !== 0) {
    return { ok: false, reason: 'These walls don’t form one continuous chain.' }
  }
  // closed loop of segments (0 free ends): start anywhere — result is an open path with
  // coincident ends, exactly what drawing a closed chain produces.
  const start = freeEnds.length ? freeEnds[0] : { seg: 0, end: 0 as const }
  const used = new Set<number>()
  const chain: ChainSeg[] = []
  let cur = start.seg
  let entryEnd: 0 | 1 = start.end
  for (;;) {
    const s = segs[cur]
    used.add(cur)
    if (entryEnd === 1) { s.nodes = [...s.nodes].reverse(); s.reversed = true }
    chain.push(s)
    const exitPt = s.nodes[s.nodes.length - 1].pt
    const nextList = (endpointUse.get(keyOf(exitPt)) || []).filter((e) => !used.has(e.seg))
    if (!nextList.length) break
    cur = nextList[0].seg
    entryEnd = nextList[0].end
  }
  if (used.size !== segs.length) {
    return { ok: false, reason: 'These walls don’t form one continuous chain.' }
  }

  // Orient the chain to the SURVIVOR's drawing direction (elementIds[0] — the user's
  // primary selection): if the walk happened to traverse it backwards, reverse the
  // whole chain so welding never flips the wall the user started from.
  const survivorId = elementIds[0]
  const survivorSeg = chain.find((s) => s.id === survivorId)
  if (survivorSeg?.reversed) {
    chain.reverse()
    for (const s of chain) {
      s.nodes = [...s.nodes].reverse()
      s.reversed = !s.reversed
    }
  }

  // Geometric facing compatibility: each segment's effective centerline offset under
  // the final chain orientation must match the survivor's (see the doc comment above).
  const type = getType(model, first.typeId)
  const assembly = type?.assemblyId ? getAssembly(model, type.assemblyId) : null
  const deltaEff = (s: ChainSeg) => {
    const r = recs.find((x) => x.id === s.id)!
    const d = centerlineOffsetFt(r.params.locationLine, r.params.flipped, assembly)
    return s.reversed ? -d : d
  }
  const dSurv = deltaEff(chain.find((s) => s.id === survivorId)!)
  if (chain.some((s) => Math.abs(deltaEff(s) - dSurv) > 1e-9)) {
    return { ok: false, reason: 'These walls face opposite directions — Flip one to match, then join.' }
  }

  // Concatenate: drop the first node of each subsequent segment (coincident with the
  // previous last — the EARLIER node's id survives, per 06 §1.2).
  const mergedNodes: PathNode[] = [...chain[0].nodes]
  for (let i = 1; i < chain.length; i++) mergedNodes.push(...chain[i].nodes.slice(1))
  const prefix: number[] = []
  let acc = 0
  for (const s of chain) { prefix.push(acc); acc += s.lengthFt }

  const { elements } = maps(model)
  transact(model, () => {
    const sem = elements.get(survivorId)
    if (!sem) return
    const spm = sem.get('params') as Y.Map<unknown>
    spm.set('path', { closed: false, nodes: mergedNodes })
    for (let i = 0; i < chain.length; i++) {
      const seg = chain[i]
      for (const op of hostedElements(model, seg.id)) {
        const oem = elements.get(op.id)
        if (!oem) continue
        const sLocal = typeof op.params.hostOffsetFt === 'number' ? op.params.hostOffsetFt : 0
        const s = seg.reversed ? seg.lengthFt - sLocal : sLocal
        oem.set('hostId', survivorId)
        const opm = oem.get('params') as Y.Map<unknown>
        opm.set('hostOffsetFt', prefix[i] + s)
      }
      if (seg.id !== survivorId) elements.delete(seg.id)
    }
  })
  return { ok: true, id: survivorId }
}

/**
 * Corner-join two walls (2.6 polish — trim/fillet with radius 0): extend/trim BOTH
 * walls so they meet at the intersection of wall A's END segment line and wall B's
 * segment line nearest to `nearPt` (the user's pick click). A's end node always moves;
 * B's nearer endpoint moves too when it is an END node of an open path (a click on an
 * interior segment leaves B untouched — that's plain extend/trim of A). One transaction.
 */
export function cornerJoin(
  model: ModelDoc,
  idA: ElementId,
  endNodeA: NodeId,
  idB: ElementId,
  nearPt: Pt2,
): { ok: true } | { ok: false; reason: string } {
  const a = getElement(model, idA)
  const b = getElement(model, idB)
  if (!a || !b) return { ok: false, reason: 'One of the walls no longer exists.' }
  // MODIFY PHASE-2 (Pin): a pinned FIRST wall can't adjust at all; a pinned target
  // just declines to move its own end (bMoves below), same as an interior-segment pick
  if (a.params.pinned === true) return { ok: false, reason: 'That wall is pinned — unpin it to adjust.' }
  const pa = a.params.path as Path2D | null
  // P6-clean: the join TARGET's plan geometry comes from the registry declaration
  // (ad-hoc path/boundary probing retired; probe kept only as a fallback)
  const pbKey = pathParamKeyFor(b.category)
  const pb = ((pbKey && b.params[pbKey]) || b.params.path || b.params.boundary) as Path2D | null
  if (!pa || pa.closed || !pa.nodes || pa.nodes.length < 2) return { ok: false, reason: 'This wall has no adjustable end.' }
  if (!pb || !pb.nodes || pb.nodes.length < 2) return { ok: false, reason: 'That element has no line to join to.' }
  const ia = nodeIndex(pa, endNodeA)
  if (ia !== 0 && ia !== pa.nodes.length - 1) return { ok: false, reason: 'Corner join works from a wall END.' }
  const aEnd = pa.nodes[ia].pt
  const aNbr = pa.nodes[ia === 0 ? 1 : pa.nodes.length - 2].pt

  // B's segment nearest the pick
  let best: { i: number; j: number; d: number } | null = null
  const nb = pb.nodes.length
  const nSegs = pb.closed ? nb : nb - 1
  for (let i = 0; i < nSegs; i++) {
    const p = pb.nodes[i].pt, q = pb.nodes[(i + 1) % nb].pt
    const dx = q[0] - p[0], dy = q[1] - p[1]
    const len2 = dx * dx + dy * dy
    if (len2 < 1e-12) continue
    let t = ((nearPt[0] - p[0]) * dx + (nearPt[1] - p[1]) * dy) / len2
    t = Math.max(0, Math.min(1, t))
    const d = Math.hypot(nearPt[0] - (p[0] + dx * t), nearPt[1] - (p[1] + dy * t))
    if (!best || d < best.d) best = { i, j: (i + 1) % nb, d }
  }
  if (!best) return { ok: false, reason: 'That element has no line to join to.' }
  const bp = pb.nodes[best.i].pt, bq = pb.nodes[best.j].pt

  // intersection of A's end-segment line with B's picked segment line
  const d1: Pt2 = [aEnd[0] - aNbr[0], aEnd[1] - aNbr[1]]
  const d2: Pt2 = [bq[0] - bp[0], bq[1] - bp[1]]
  const l1 = Math.hypot(d1[0], d1[1]), l2 = Math.hypot(d2[0], d2[1])
  if (l1 < 1e-9 || l2 < 1e-9) return { ok: false, reason: 'Degenerate segment.' }
  const cross = d1[0] * d2[1] - d1[1] * d2[0]
  if (Math.abs(cross / (l1 * l2)) < 1e-3) {
    return { ok: false, reason: 'The walls are parallel — nothing to corner-join.' }
  }
  const t = ((bp[0] - aNbr[0]) * d2[1] - (bp[1] - aNbr[1]) * d2[0]) / cross
  const hit: Pt2 = [aNbr[0] + d1[0] * t, aNbr[1] + d1[1] * t]

  // B's nearer endpoint of the picked segment — trims/extends only when it's an END
  // node of an open path (never drag an interior corner or a closed boundary node)
  const dToI = Math.hypot(hit[0] - bp[0], hit[1] - bp[1])
  const dToJ = Math.hypot(hit[0] - bq[0], hit[1] - bq[1])
  const bIdx = dToI <= dToJ ? best.i : best.j
  const bMoves = !pb.closed && (bIdx === 0 || bIdx === nb - 1) && b.category === 'wall'
    && b.params.pinned !== true   // MODIFY PHASE-2 (Pin)

  const { elements } = maps(model)
  const emA = elements.get(idA)
  const emB = elements.get(idB)
  if (!emA || !emB) return { ok: false, reason: 'One of the walls no longer exists.' }

  const newA = clonePath(pa)
  newA.nodes[ia].pt = [hit[0], hit[1]]
  const newB = bMoves ? clonePath(pb) : null
  if (newB) newB.nodes[bIdx].pt = [hit[0], hit[1]]

  // WALL JOINS: this pulls A's (and maybe B's) end to a new point — dry-run the join
  // graph off BOTH proposed paths first (same doctrine as writePath/translateElement)
  // so a corner join that would collapse some OTHER wall already joined to A or B
  // refuses cleanly, mutating nothing, instead of leaving a half-applied corner.
  const planA: Map<ElementId, Path2D> | null = a.category === 'wall' ? planWallJoinPropagation(model, idA, newA) : new Map()
  if (a.category === 'wall' && planA === null) {
    return { ok: false, reason: 'This would collapse a wall joined to it — disconnect the join first.' }
  }
  const planB: Map<ElementId, Path2D> | null = newB && b.category === 'wall' ? planWallJoinPropagation(model, idB, newB) : new Map()
  if (newB && b.category === 'wall' && planB === null) {
    return { ok: false, reason: 'This would collapse a wall joined to the target — disconnect the join first.' }
  }

  transact(model, () => {
    ;(emA.get('params') as Y.Map<unknown>).set('path', newA)
    rehostOpeningsForPathChange(model, idA, pa, newA)
    if (a.category === 'wall') {
      applyJoinPropagation(model, planA!)
      autoDetectWallJoinAtNode(model, idA, newA.nodes[ia].id)
    }
    if (newB) {
      ;(emB.get('params') as Y.Map<unknown>).set('path', newB)
      rehostOpeningsForPathChange(model, idB, pb, newB)
      if (b.category === 'wall') {
        applyJoinPropagation(model, planB!)
        autoDetectWallJoinAtNode(model, idB, newB.nodes[bIdx].id)
      }
    }
  })
  return { ok: true }
}

/**
 * Offset-copy a wall (2.6 polish): a NEW wall element parallel to this one at
 * `distanceFt` along the left normal (negative = right), original untouched. The copy
 * clones type/level/instance params with FRESH node ids; hosted openings are NOT copied
 * (v1 — an opening belongs to one host). Returns the new element id.
 */
export function offsetCopyWall(model: ModelDoc, id: ElementId, distanceFt: number): ElementId | null {
  const rec = getElement(model, id)
  if (!rec || rec.category !== 'wall') return null
  const path = rec.params.path as Path2D | null
  if (!path || !path.nodes || path.nodes.length < 2) return null
  const moved = offsetPolyline(path, distanceFt)
  if (!moved) return null
  const fresh: Path2D = {
    closed: moved.closed,
    nodes: moved.nodes.map((n) => ({ ...n, id: newNodeId() })),
  }
  const newId = newElementId()
  const em = maps(model).elements.get(id)
  if (!em) return null
  const params = readParams(em)
  transact(model, () => {
    maps(model).elements.set(newId, recordToYMap({
      id: newId,
      category: 'wall',
      typeId: rec.typeId,
      levelId: rec.levelId,
      // a copy / split / offset keeps the ORIGINAL's phasing (Matt 2026-09-29: "the new
      // element created by the command should have the same phasing properties")
      ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
      ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
      params: { ...params, path: fresh },
    }))
  })
  return newId
}

/**
 * Change a wall's location line WITHOUT moving the wall (06 §4.3): the stored path is
 * transformed by the offset delta (old centerline offset − new) in the same transaction
 * as the param write, so the body stays put and only the reference designation moves.
 */
export function setLocationLine(model: ModelDoc, elementId: ElementId, value: LocationLine): boolean {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'wall') return false
  if (rec.params.pinned === true) return false   // MODIFY PHASE-2 (Pin)
  const path = rec.params.path as Path2D | null
  if (!path || typeof path !== 'object' || !Array.isArray(path.nodes)) return false
  const type = getType(model, rec.typeId)
  const assembly = type?.assemblyId ? getAssembly(model, type.assemblyId) : null
  const flipped = rec.params.flipped
  const deltaOld = centerlineOffsetFt(rec.params.locationLine, flipped, assembly)
  const deltaNew = centerlineOffsetFt(value, flipped, assembly)
  const moved = offsetPolyline(path, deltaOld - deltaNew)
  if (!moved) return false
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  // WALL JOINS: the body slides sideways, so both ends move too — propagate like any
  // other whole-path rewrite (refuses, mutating nothing, if it would collapse a joined
  // wall). No auto-detect (v1 scope, same as translate/rotate/mirror).
  const joinPlan = planWallJoinPropagation(model, elementId, moved)
  if (joinPlan === null) return false
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    pm.set('locationLine', value)
    pm.set('path', moved)
    // the body doesn't move, so openings shouldn't either: re-anchor by world position
    // (offset paths have slightly different arc lengths around corners)
    rehostOpeningsForPathChange(model, elementId, path, moved)
    applyJoinPropagation(model, joinPlan)
  })
  return true
}

/**
 * Flip a wall about its location line (06 §4.3) by toggling the `flipped` instance param
 * — node ids and every opening's hostOffsetFt stay untouched (the reason this is a param,
 * not a node-order reversal). Geometry mirrors the body to the other side of the stored
 * path; an involution by construction.
 */
export function flipWall(model: ModelDoc, elementId: ElementId): boolean {
  const em = maps(model).elements.get(elementId)
  if (!em || em.get('category') !== 'wall') return false
  const pm0 = em.get('params') as Y.Map<unknown>
  if (pm0.get('pinned') === true) return false   // MODIFY PHASE-2 (Pin): flip moves the body
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    pm.set('flipped', !pm.get('flipped'))
  })
  return true
}

/**
 * MODIFY PHASE-2 — Split with Gap (the Revit tool): split a wall at a point on an edge
 * AND pull the two fresh ends back `gapFt` apart (half each side), one transaction =
 * one undo step. Refuses (null, mutating nothing) when either resulting run would be
 * consumed by its half-gap, and on an ARC edge (a gap trim there needs arc-length
 * surgery along the circle — plain Split still works on arcs; recorded v1 scope).
 * Openings re-anchor through moveNode's own writePath re-hosting like any shape edit.
 */
export function splitWallWithGap(
  model: ModelDoc,
  elementId: ElementId,
  at: { edge: EdgeRef; pt: Pt2 },
  gapFt: number,
): ElementId[] | null {
  const rec = getElement(model, elementId)
  if (!rec || rec.category !== 'wall') return null
  if (rec.params.pinned === true) return null
  const path = rec.params.path as Path2D | null
  if (!path || path.closed || !Array.isArray(path.nodes) || path.nodes.length < 2) return null
  const idx = edgeIndices(path, at.edge)
  if (!idx) return null
  if (path.nodes[idx[0]].bulge) return null   // arc edge — v1 refuses (see doc comment)
  const a = path.nodes[idx[0]].pt
  const b = path.nodes[idx[1]].pt
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return null
  const d: Pt2 = [dx / len, dy / len]
  const half = Math.max(0, gapFt) / 2
  const MIN_RUN = 0.05
  if (Math.hypot(at.pt[0] - a[0], at.pt[1] - a[1]) - half < MIN_RUN) return null
  if (Math.hypot(b[0] - at.pt[0], b[1] - at.pt[1]) - half < MIN_RUN) return null
  let out: ElementId[] | null = null
  transact(model, () => {
    const ids = splitWall(model, elementId, { edge: at.edge, pt: at.pt })
    if (!ids || ids.length < 2) return
    // the original keeps the EARLIER run (its last node is the cut); the new element's
    // first node is the duplicated cut node — pull each back half the gap along the edge
    const p1 = getElement(model, ids[0])?.params.path as Path2D | null
    const p2 = getElement(model, ids[1])?.params.path as Path2D | null
    if (!p1?.nodes?.length || !p2?.nodes?.length) return
    const okA = moveNode(model, ids[0], 'path', p1.nodes[p1.nodes.length - 1].id,
      [at.pt[0] - d[0] * half, at.pt[1] - d[1] * half])
    const okB = moveNode(model, ids[1], 'path', p2.nodes[0].id,
      [at.pt[0] + d[0] * half, at.pt[1] + d[1] * half])
    if (okA && okB) out = ids
  })
  return out
}
